/**
 * Save orchestration for the UW Information form.
 *
 * The v2 backend exposes one endpoint per resource (top-level
 * submission + four child collections + contact 1:1). The form on the
 * other hand round-trips a single grouped object, so this module owns
 * the reduction:
 *
 *   - **Edit**   PATCH submission top-level → diff each child list
 *                (POST new, PATCH changed, DELETE removed) → PUT
 *                contact → re-GET so timestamps + ids are fresh.
 *   - **Create** POST submission first → POST every child row + PUT
 *                contact in parallel → re-GET for the canonical state.
 *
 * Failures bubble up as ApiError. The caller (UW page) is responsible
 * for restoring the previous form state on error.
 */
import {
    createAdditionalInsured,
    createNamedInsured,
    createSubmission,
    deleteAdditionalInsured,
    deleteNamedInsured,
    fromWireSubmission,
    getSubmission,
    patchAdditionalInsured,
    patchNamedInsured,
    patchSubmission,
    putContact,
    toWireAdditionalInsured,
    toWireContact,
    toWireNamedInsured,
    toWireSubmission,
} from "../../../api";
import { writeDecFields } from "../../../api/dec-fields-local";
import type { AdditionalInsuredForm, NamedInsuredForm, SubmissionForm } from "../../../types/submission";
import type { UwInformationFormData } from "./schema";

/** Merge form data back into the long-lived `SubmissionForm` so the
 *  layout's header (status / assignee / approver) survives a save. */
export function mergeFormIntoDetail(detail: SubmissionForm, form: UwInformationFormData): SubmissionForm {
    return {
        ...detail,
        accountBasics: form.accountBasics,
        brokerage: form.brokerage,
        geminiContacts: form.geminiContacts,
        note: form.note,
        namedInsureds: form.namedInsureds,
        additionalInsureds: form.additionalInsureds,
        // Locations aren't part of the UW Info form anymore (owned by the
        // Schedule section) — carry the existing detail value untouched so
        // the long-lived SubmissionForm shape stays intact.
        locations: detail.locations,
    };
}

interface ChildDiff<T> {
    create: T[];
    update: T[];
    deleteIds: string[];
}

/** Split a child list into (create, update, deleteIds). New rows are
 *  the ones whose `id` was generated client-side (`*-new-*`) — see
 *  `clientRowId()` in schema.ts. */
function diffChildren<T extends { id: string }>(prev: T[], next: T[]): ChildDiff<T> {
    const prevIds = new Set(prev.map((r) => r.id));
    const nextIds = new Set(next.map((r) => r.id));

    const create = next.filter((r) => isClientRowId(r.id));
    const update = next.filter((r) => !isClientRowId(r.id) && prevIds.has(r.id));
    const deleteIds = [...prevIds].filter((id) => !nextIds.has(id) && !isClientRowId(id));

    return { create, update, deleteIds };
}

function isClientRowId(id: string): boolean {
    return id === "" || /-new-\d+-\d+$/.test(id);
}

// ─────────────────────────── Save (edit) ──────────────────────

export async function saveEdit(submissionId: string, previous: SubmissionForm, next: UwInformationFormData): Promise<SubmissionForm> {
    const merged = mergeFormIntoDetail(previous, next);

    // 1. PATCH the top-level submission with the flat payload.
    await patchSubmission(submissionId, toWireSubmission(merged));

    // OR-1455 — the backend drops `mailing_address` until PR #326 deploys, so
    // keep it locally or the re-GET below would blank what was just typed.
    // No-op behind the flag; remove with `api/dec-fields-local`.
    writeDecFields(submissionId, {
        mailingAddress: merged.accountBasics.mailingAddress,
        businessDescription: merged.accountBasics.businessDescription,
    });

    // 2. PUT contact (1:1 upsert — server collapses absent fields to
    //    null, which matches what the form would send anyway).
    await putContact(submissionId, toWireContact(next.geminiContacts));

    // 3. Diff & apply each child collection.
    // Locations are NOT diffed/saved here — the Schedule section owns them.
    const niDiff = diffChildren(previous.namedInsureds, next.namedInsureds);
    const aiDiff = diffChildren(previous.additionalInsureds, next.additionalInsureds);

    await Promise.all([
        ...niDiff.deleteIds.map((id) => deleteNamedInsured(submissionId, id)),
        ...aiDiff.deleteIds.map((id) => deleteAdditionalInsured(submissionId, id)),
    ]);

    await Promise.all([
        ...niDiff.update.map((row) => patchNamedInsured(submissionId, row.id, toWireNamedInsured(row))),
        ...aiDiff.update.map((row) => patchAdditionalInsured(submissionId, row.id, toWireAdditionalInsured(row))),
    ]);

    await Promise.all([
        ...niDiff.create.map((row: NamedInsuredForm) => createNamedInsured(submissionId, toWireNamedInsured(row))),
        ...aiDiff.create.map((row: AdditionalInsuredForm) => createAdditionalInsured(submissionId, toWireAdditionalInsured(row))),
    ]);

    // 4. Re-GET so the form sees server-assigned ids + timestamps.
    const refreshed = await getSubmission(submissionId);
    return fromWireSubmission(refreshed);
}

// ─────────────────────────── Save (create) ────────────────────

export async function saveCreate(previous: SubmissionForm, next: UwInformationFormData): Promise<SubmissionForm> {
    const merged = mergeFormIntoDetail(previous, next);

    // 1. POST the submission shell. Children and contact are written
    //    next; we don't try to do this in one round-trip because the
    //    backend doesn't expose a bulk create.
    const created = await createSubmission(toWireSubmission(merged));
    const submissionId = created.id;

    // OR-1455 — same local stand-in as `saveEdit`; the id only exists now.
    writeDecFields(submissionId, {
        mailingAddress: merged.accountBasics.mailingAddress,
        businessDescription: merged.accountBasics.businessDescription,
    });

    // 2. Children + contact in parallel.
    await Promise.all([
        putContact(submissionId, toWireContact(next.geminiContacts)),
        ...next.namedInsureds.map((row) => createNamedInsured(submissionId, toWireNamedInsured(row))),
        ...next.additionalInsureds.map((row) => createAdditionalInsured(submissionId, toWireAdditionalInsured(row))),
    ]);

    // 3. Re-GET so the form sees the canonical server view.
    const refreshed = await getSubmission(submissionId);
    return fromWireSubmission(refreshed);
}
